import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import URLConstants from '../../constants/url-constants';
import {
    IAddCheckItemTemplate,
    ICheckItemTemplateByProcessId,
    IUpdateCheckItemTemplate,
} from '../../models/forms/ICheckItemTemplate';
import IApiResponse, {
    IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import { ICheckItemTemplateListVM } from '../../models/response/ICheckItemTemplateResponse';
import { formatMessage } from '../../translations/format-message';
import {
    AddCheckItemTemplateInitialState,
    GetAllCheckItemTemplateInitialState,
    ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import { ICheckItemFilter } from '../../models/ICheckItemFilter';

export class CheckItemTemplateStore {
    initialStateValue = ObservableInitialState;
    checkItemTemplateList: ICheckItemTemplateListVM =
        GetAllCheckItemTemplateInitialState;
    addUpdateCheckItemTemplateState = { ...this.initialStateValue };
    deleteCheckItemTemplateState = { ...this.initialStateValue };
    getAllCheckItemTemplateState = { ...this.initialStateValue };
    checkItemTemplateDetail: IAddCheckItemTemplate =
        AddCheckItemTemplateInitialState;
    checkItemTemplateByProcessIdDetail: ICheckItemTemplateByProcessId[] = [];
    getCheckItemTemplateByIdState = { ...this.initialStateValue };
    getCheckItemTemplateByProcessIdState = { ...this.initialStateValue };

    constructor() {
        makeObservable(this, {
            checkItemTemplateList: observable,
            addUpdateCheckItemTemplateState: observable,
            deleteCheckItemTemplateState: observable,
            getAllCheckItemTemplateState: observable,
            checkItemTemplateDetail: observable,
            getCheckItemTemplateByIdState: observable,
            getCheckItemTemplateByProcessIdState: observable,
            checkItemTemplateByProcessIdDetail: observable,
            GetCheckItemTemplateListService: action,
            AddCheckItemTemplateService: action,
            UpdateCheckItemTemplateService: action,
            DeleteCheckItemTemplateService: action,
            GetCheckItemTemplateByIdService: action,
            GetCheckItemTemplateByProcessIdService: action,
            resetAddUpdateState: action,
            resetDeleteState: action,
            reset: action,
            getCheckItemTemplateDetail: computed,
            getCheckItemTemplateByProcessIdDetail: computed
        });
    }

    GetCheckItemTemplateListService = (
        tenantId: number,
        currentPage: number,
        pageLimit: number,
        searchTerm: ICheckItemFilter
    ) => {
        this.getAllCheckItemTemplateState.inProgress = true;
        const url = URLConstants.GetCheckItemTemplateList;
        const data = {
            TenantId: tenantId,
            PagingDetail: {
                CurrentPage: currentPage,
                PageSize: pageLimit,
                TotalPages: 0,
                TotalRecords: 0,
            },
            checkItemFilter: searchTerm,
        };
        return baseService
            .postRequest(url, data)
            .then(
                (
                    response: IApiResponse<IApiSuccessResponse<ICheckItemTemplateListVM>>
                ) => {
                    if (response.data.Error) {
                        this.getAllCheckItemTemplateState.error = response.data.Message;
                        toast.error(formatMessage(response.data.Message));
                    } else {
                        this.checkItemTemplateList = response.data.Data;
                    }
                }
            )
            .catch((err: any) => {
                this.getAllCheckItemTemplateState.error = err;
            })
            .finally(
                action(() => {
                    this.getAllCheckItemTemplateState.inProgress = false;
                })
            );
    };

    AddCheckItemTemplateService = (data: IAddCheckItemTemplate) => {
        this.addUpdateCheckItemTemplateState.inProgress = true;
        return baseService
            .postRequest(URLConstants.AddCheckItemTemplate, data)
            .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
                if (response.data.Error) {
                    this.addUpdateCheckItemTemplateState.error = response.data.Message;
                    toast.error(formatMessage(response.data.Message));
                } else {
                    this.addUpdateCheckItemTemplateState.success = true;
                    toast.success(formatMessage('added_success'));
                }
            })
            .catch((err: any) => {
                this.addUpdateCheckItemTemplateState.error = err;
            })
            .finally(
                action(() => {
                    this.addUpdateCheckItemTemplateState.inProgress = false;
                })
            );
    };

    UpdateCheckItemTemplateService = (data: IUpdateCheckItemTemplate) => {
        this.addUpdateCheckItemTemplateState.inProgress = true;
        return baseService
            .putRequest(URLConstants.UpdateCheckItemTemplate, data)
            .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
                if (response.data.Error) {
                    this.addUpdateCheckItemTemplateState.error = response.data.Message;
                    toast.error(formatMessage(response.data.Message));
                } else {
                    this.addUpdateCheckItemTemplateState.success = true;
                    toast.success(formatMessage('updated_success'));
                }
            })
            .catch((err: any) => {
                this.addUpdateCheckItemTemplateState.error = err;
            })
            .finally(
                action(() => {
                    this.addUpdateCheckItemTemplateState.inProgress = false;
                })
            );
    };

    DeleteCheckItemTemplateService = (tenantId: number, id: number) => {
        this.deleteCheckItemTemplateState.inProgress = true;
        const url = `${URLConstants.DeleteCheckItemTemplate}?TenantId=${tenantId}&TemplateId=${id}`;
        return baseService
            .deleteRequest(url)
            .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
                if (response.data.Error) {
                    this.deleteCheckItemTemplateState.error = response.data.Message;
                    toast.error(formatMessage(response.data.Message));
                } else {
                    this.deleteCheckItemTemplateState.success = true;
                }
            })
            .catch((err: any) => {
                this.deleteCheckItemTemplateState.error = err;
            })
            .finally(
                action(() => {
                    this.deleteCheckItemTemplateState.inProgress = false;
                })
            );
    };

    GetCheckItemTemplateByIdService = (tenantId: number, id: number) => {
        this.getCheckItemTemplateByIdState.inProgress = true;
        const url = `${URLConstants.GetCheckItemTemplateById}?TenantId=${tenantId}&TemplateId=${id}`;
        return baseService
            .getRequest(url)
            .then(
                (
                    response: IApiResponse<IApiSuccessResponse<IAddCheckItemTemplate>>
                ) => {
                    if (response.data.Error) {
                        this.getCheckItemTemplateByIdState.error = response.data.Message;
                        toast.error(formatMessage(response.data.Message));
                    } else {
                        this.checkItemTemplateDetail = response.data.Data;
                    }
                }
            )
            .catch((err: any) => {
                this.getCheckItemTemplateByIdState.error = err;
            })
            .finally(
                action(() => {
                    this.getCheckItemTemplateByIdState.inProgress = false;
                })
            );
    };

    GetCheckItemByTemplateIdService = (tenantId: number, id: number, values: any, setFieldValue: any, selectedProcessIndex: any, selectedImageIndex: any) => {
        this.getCheckItemTemplateByIdState.inProgress = true;
        const url = `${URLConstants.GetCheckItemTemplateById}?TenantId=${tenantId}&TemplateId=${id}`;
        return baseService
            .getRequest(url)
            .then(
                (
                    response: IApiResponse<IApiSuccessResponse<IAddCheckItemTemplate>>
                ) => {
                    if (response.data.Error) {
                        this.getCheckItemTemplateByIdState.error = response.data.Message;
                        toast.error(formatMessage(response.data.Message));
                    } else {
                        const currentFileIndex = values?.Process?.[selectedProcessIndex]?.fileIndices?.[selectedImageIndex];
                        const items = (currentFileIndex && Array.isArray(currentFileIndex.CheckItems)) ? currentFileIndex.CheckItems : [];
                        const messages = (currentFileIndex && Array.isArray(currentFileIndex.CheckItemMessage)) ? currentFileIndex.CheckItemMessage : [];
                        const rawExisting = [...items, ...messages];

                        // Map to track existing messages per header for accurate deduplication
                        const existingMsgsByHeader = new Map<string, Set<string>>();

                        rawExisting.forEach((item: any) => {
                            const nested = Array.isArray(item.CheckItems) ? item.CheckItems : item.CheckItemMessage;
                            if (Array.isArray(nested)) {
                                const headerTitle = (item.Title || item.HeaderTitle || 'Default').trim().toLowerCase();
                                if (!existingMsgsByHeader.has(headerTitle)) {
                                    existingMsgsByHeader.set(headerTitle, new Set());
                                }
                                nested.forEach((n: any) => {
                                    const msg = (n.CheckMessage || n.CheckItemMessage || n.text || '').trim().toLowerCase();
                                    if (msg) existingMsgsByHeader.get(headerTitle)!.add(msg);
                                });
                            } else {
                                const headerTitle = (item.HeaderTitle || item.Title || 'Default').trim().toLowerCase();
                                if (!existingMsgsByHeader.has(headerTitle)) {
                                    existingMsgsByHeader.set(headerTitle, new Set());
                                }
                                const msg = (item.CheckMessage || item.CheckItemMessage || item.text || '').trim().toLowerCase();
                                if (msg) existingMsgsByHeader.get(headerTitle)!.add(msg);
                            }
                        });


                        const responseData = response.data.Data;
                        let templateItems: any[] = [];

                        const rawData = responseData as any;
                        const rawHeaders = rawData.Header || rawData.Headers || (Array.isArray(rawData.CheckItems) && (rawData.CheckItems[0]?.Title || rawData.CheckItems[0]?.CheckItemMessage || rawData.CheckItems[0]?.CheckMessage) ? rawData.CheckItems : []) || [];
                        if (rawHeaders.length > 0) {
                            rawHeaders.forEach((header: any) => {
                                const rawItems = header.CheckItemMessage || header.CheckItems || [];
                                rawItems.forEach((item: any) => {
                                    const message = item.CheckItemMessage || item.CheckMessage || '';
                                    if (message) {
                                        templateItems.push({
                                            CheckMessage: message,
                                            IsChecked: !!item.IsChecked,
                                            IsDisabled: true,
                                            HeaderTitle: header.ChechItemHeader || header.Title || 'Default'
                                        });
                                    }
                                });
                            });
                        } else {
                            const rawItems = responseData?.CheckItemMessage || (responseData as any)?.CheckItems || [];
                            templateItems = rawItems
                                .filter(item => {
                                    const msg = item.CheckItemMessage || item.CheckMessage || "";
                                    return !(item.Id === -1 && msg === "");
                                })
                                .map(item => ({
                                    CheckMessage: item.CheckItemMessage || item.CheckMessage || "",
                                    IsChecked: !!(item.IsChecked || item.IsInfo),
                                    IsDisabled: true,
                                    HeaderTitle: item.HeaderTitle || 'Default'
                                }));
                        }

                        // Preserve all existing items and only add missing ones from the template
                        const filteredNewItems = templateItems.filter(item => {
                            const newItemMsg = (item.CheckMessage || item.CheckItemMessage || '').trim().toLowerCase();
                            if (!newItemMsg) return false;

                            const headerTitle = (item.HeaderTitle || 'Default').trim().toLowerCase();
                            return !existingMsgsByHeader.get(headerTitle)?.has(newItemMsg);
                        });

                        const addedCount = filteredNewItems.length;
                        const duplicateCount = Math.max(templateItems.length - addedCount, 0);

                        setFieldValue(
                            `Process.${selectedProcessIndex}.fileIndices.${selectedImageIndex}.CheckItems`,
                            [
                                ...rawExisting,
                                ...filteredNewItems
                            ]
                        );

                        if (templateItems.length > 0) {
                            toast.success(formatMessage('template_check_items_added', { count: addedCount }));
                        }

                        if (duplicateCount > 0) {
                            toast(formatMessage('duplicate_items_ignored'));
                        }
                    }
                }
            )
            .catch((err: any) => {
                this.getCheckItemTemplateByIdState.error = err;
            })
            .finally(
                action(() => {
                    this.getCheckItemTemplateByIdState.inProgress = false;
                })
            );
    };

    GetCheckItemTemplateByProcessIdService = (tenantId: number, id: number) => {
        this.getCheckItemTemplateByProcessIdState.inProgress = true;
        const url = `${URLConstants.GetCheckItemListByProcessId}?TenantId=${tenantId}&ProcessId=${id}`;
        return baseService
            .getRequest(url)
            .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
                if (response.data.Error) {
                    this.getCheckItemTemplateByProcessIdState.error = response.data.Message;
                    toast.error(formatMessage(response.data.Message));
                } else {
                    this.checkItemTemplateByProcessIdDetail = response.data.Data?.CheckItems || [];
                }
            })
            .catch((err: any) => {
                this.getCheckItemTemplateByProcessIdState.error = err;
            })
            .finally(action(() => {
                this.getCheckItemTemplateByProcessIdState.inProgress = false;
            }));
    };

    get getCheckItemTemplateDetail() {
        const detail = this.checkItemTemplateDetail;
        const rawHeaders = (detail as any).Header || detail.Headers || [];
        if (rawHeaders.length > 0) {
            const allItems: any[] = [];
            rawHeaders.forEach((h: any) => {
                const rawItems = h.CheckItemMessage || h.CheckItems || [];
                rawItems.forEach((i: any) => {
                    allItems.push({
                        CheckMessage: i.CheckItemMessage,
                        IsChecked: i.IsChecked || false,
                        IsDisabled: true,
                        HeaderTitle: h.ChechItemHeader || h.Title
                    });
                });
            });
            return allItems;
        }

        return (detail.CheckItemMessage || [])
            .filter((item) => !(item.Id === -1 && item.CheckItemMessage === ""))
            .map((item) => ({
                CheckMessage: item.CheckItemMessage,
                IsChecked: item.IsChecked || false,
                IsDisabled: true,
                HeaderTitle: item.HeaderTitle
            }));
    }
    get getCheckItemTemplateByProcessIdDetail() {
        return this.checkItemTemplateByProcessIdDetail;
    }

    resetAddUpdateState = () => {
        this.addUpdateCheckItemTemplateState = { ...this.initialStateValue };
    };

    resetDeleteState = () => {
        this.deleteCheckItemTemplateState = { ...this.initialStateValue };
    };

    resetDetail = () => {
        this.checkItemTemplateDetail = AddCheckItemTemplateInitialState;
        this.getCheckItemTemplateByIdState = { ...this.initialStateValue };
    };

    reset = () => {
        this.checkItemTemplateList = GetAllCheckItemTemplateInitialState;
        this.checkItemTemplateDetail = AddCheckItemTemplateInitialState;
    };
}

export default new CheckItemTemplateStore();
